import { useEffect, useState, type ReactNode } from 'react';
import { FileText, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FileViewerContent } from '@/components/file-viewer/file-viewer-content';
import { useFeatureFlag } from '@/lib/feature-flags';
import { FeatureFlag } from '@metycle/shared';

import { usePreviewInvoicePdf } from '../api/use-preview-invoice-pdf';

import type { InvoiceRow } from '../types';

function previewButton(
  isPending: boolean,
  hasPreview: boolean,
): { icon: ReactNode; label: string } {
  if (isPending) {
    return {
      icon: <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />,
      label: 'Generating preview…',
    };
  }
  if (!hasPreview) {
    return { icon: <FileText className="mr-2 h-4 w-4" aria-hidden />, label: 'Preview PDF' };
  }
  return { icon: <RefreshCw className="mr-2 h-4 w-4" aria-hidden />, label: 'Refresh preview' };
}

// Draft preview so Ops can verify the PDF before the irreversible issue step.
// The preview endpoint persists nothing and never touches ZUGFeRD/DATEV.
export function DraftInvoicePreviewSection({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  const enabled = useFeatureFlag(FeatureFlag.INVOICING_V0, false);
  const isDraft = invoice.status === 'Draft';
  const isSell = invoice.invoice_direction === 'Sale';
  const preview = usePreviewInvoicePdf(invoice.id);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (url === null) return undefined;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [url]);

  if (!enabled || !isDraft || !isSell) return null;

  const runPreview = () => {
    setUrl(null); // clear first so a failed re-render never leaves stale bytes
    const applyBlob = (blob: Blob) => {
      setUrl(URL.createObjectURL(blob));
    };
    // The draft section always previews the row's own value, never chain history.
    preview.mutate({ includeChainHistory: false }, { onSuccess: applyBlob });
  };

  const { icon, label } = previewButton(preview.isPending, url !== null);

  return (
    <section aria-labelledby="draft-preview-heading" className="space-y-2">
      <h3 id="draft-preview-heading" className="text-sm font-semibold">
        DOCUMENT PREVIEW
      </h3>
      <p className="text-muted-foreground text-sm">
        Review the generated invoice PDF before issuing. This is a draft preview — nothing is sent
        to DATEV until you issue.
      </p>
      <Button
        variant={url === null ? 'default' : 'outline'}
        onClick={runPreview}
        disabled={preview.isPending}
        aria-busy={preview.isPending}
        data-testid="draft-preview-button"
      >
        {icon}
        {label}
      </Button>
      <PreviewResult
        error={preview.isError ? preview.error.message : null}
        url={url}
        fileName={`invoice-${invoice.id}-preview.pdf`}
      />
    </section>
  );
}

function PreviewResult({
  error,
  url,
  fileName,
}: Readonly<{ error: string | null; url: string | null; fileName: string }>) {
  return (
    <>
      {error !== null ? (
        <p className="text-destructive text-sm" role="alert">
          Could not generate the preview. {error}
        </p>
      ) : null}
      {url !== null ? (
        <div
          className="h-[520px] overflow-auto rounded-md border"
          data-testid="draft-preview-viewer"
        >
          <FileViewerContent fileName={fileName} mimeType="application/pdf" url={url} />
        </div>
      ) : null}
    </>
  );
}
